import React from "react";
import Seo from "../components/Seo";
import Loading from "../components/Loading";
import "../css/Documentation.css";
import {BrokerBenchmark, parseBrokerResult} from "../components/BrokerBenchmark";
import persistenceUrl from "../xmq_test_results/Persistence.txt";

export default class XMQ_tests_persistence extends React.Component
{
    state = {servers: null, error: null};

    componentDidMount()
    {
        fetch(persistenceUrl)
            .then((r) => r.text())
            .then((text) => this.setState({servers: parseBrokerResult(text)}))
            .catch((err) => this.setState({error: String(err)}));
    }

    render()
    {
        const {servers, error} = this.state;

        return <div key="tests-persistence" className="XMQ" style={{textAlign: "left", padding: 8}}>
            <Seo title="MQTT Performance Tests — Message Persistence"
                 description="MQTT performance tests of persistent message delivery: 60,000 messages a second between 120,000 persistent sessions, XMQ against other MQTT servers."
                 keywords="MQTT performance tests, MQTT persistence, fast MQTT server, XMQ"
                 path="/xmq_tests_persistence"/>
            <h1>MQTT Performance Tests: Message Persistence</h1>
            <h3>Persistent Point-to-Point: Durable Delivery</h3>

            <p>
                This test repeats one-to-one delivery with persistent sessions, each broker with
                the persistence it offers, so queued messages can survive a broker restart. The
                measured rate and latency show the cost of that durability; what a failure can
                still lose depends on the broker and its configuration - for XMQ see{" "}
                <a href="/xmq_persistence">Persistence</a>.
            </p>

            <p>
                The scenario is point-to-point, the same shape as the in-memory point-to-point
                page: each publisher has its own subscriber on its own topic, so the broker does
                no fan-out and what is measured is the per-message cost of routing plus the cost
                of persisting it. Both client groups connect with <code>clean_session</code> off,
                so a run at 60K holds 120,000 persistent sessions. Each publisher sends one
                message per second with a 16-byte payload, and every broker starts against empty
                storage.
            </p>

            <p>
                <b>This configuration answers a particular need, and is not the one most
                deployments want.</b> Persistence earns its cost where a message that never
                arrives is worse than a message that arrives late: commands to equipment that is
                intermittently connected, orders and transactions, anything a subscriber must
                receive even if it was offline when the message was sent, or if the broker
                restarted between the two. For live telemetry, metrics and status streams &mdash;
                where the next reading supersedes the last and a gap costs nothing &mdash;
                persistence buys nothing, and the in-memory figures on the other test pages are
                the ones that apply.
            </p>

            <p>
                It is worth setting the numbers below against that. Workloads that genuinely need
                durable delivery are usually counted in messages per device per minute rather
                than per second, so the rates measured here are well clear of what they ask for.
                The ceiling matters when durable and high-volume traffic share one broker, and
                the two can be separated: only sessions connecting with{" "}
                <code>clean_session</code> off and messages sent at QoS&nbsp;1 or above pay for
                persistence at all, so a broker can carry both without the fast traffic paying
                the durable traffic's cost.
            </p>

            <p>
                <b>The brokers do not persist the same thing, and that is part of the result.</b>
                XMQ writes every message to Redis (in the <a href="/xmq_persistence">Durable</a>{" "}
                mode, <code>appendfsync everysec</code>, <code>max_queued_writes</code> 1000), and
                HiveMQ uses its file persistence; both store each message. EMQX stores messages
                with durable sessions enabled. FlashMQ saves its sessions to disk periodically and
                at shutdown, and Mosquitto saves a snapshot of its in-memory database on an
                interval &mdash; so a crash loses what arrived since the last save, and their
                figures are those of in-memory delivery. Each broker's settings are recorded with
                its results.
            </p>

            <p>
                XMQ 1.0.2 and FlashMQ 1.27.2 were re-measured on 2 November 2026 on the same
                c5n.4xlarge host (16 vCPU, 40 GB) as the other brokers here. Percentiles are
                shown where the run recorded them; a dash means its result file has no percentile
                section.
            </p>

            {error && <p>Failed to load test results: {error}</p>}
            {!servers && !error && <Loading text="Loading results…"/>}

            <BrokerBenchmark servers={servers} targetRate={60000}/>
        </div>;
    }
}
